/**
 * T012: useAIPostEdit Hook
 *
 * Custom hook for AI-assisted post editing with streaming responses
 * Manages streaming responses, loading states, and error handling
 */

import { useCallback, useState, useTransition } from 'react';
import { useMutation } from 'convex/react';
import { toast } from 'sonner';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';

interface UseAIPostEditOptions {
  postId: string;
  originalContent: string;
  onSuccess?: (editedContent: string) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for AI-assisted post editing with streaming responses
 *
 * @param options - Configuration options
 * @returns AI editing state and control functions
 */
export function useAIPostEdit({
  postId,
  originalContent,
  onSuccess,
  onError,
}: UseAIPostEditOptions) {
  const [isPending, startTransition] = useTransition();
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [error, setError] = useState<Error | null>(null);
  const updatePostContent = useMutation(api.posts.updatePostContent);

  // Abort controller for cancelling ongoing requests
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  /**
   * Execute AI edit with instruction using custom streaming
   */
  const executeEdit = useCallback(
    async (instruction: string) => {
      if (!instruction.trim()) {
        const err = new Error('Instruction cannot be empty');
        setError(err);
        toast.error('Instruction required', {
          description: 'Please provide editing instructions before submitting.',
        });
        if (onError) {
          onError(err);
        }
        return;
      }

      // Show loading toast
      toast.info('AI is editing your post...', {
        description: 'This may take a few seconds.',
      });

      // Reset state
      setEditedContent('');
      setError(null);
      setIsEditing(true);

      // Create abort controller for this request
      const controller = new AbortController();
      setAbortController(controller);

      try {
        const convexSiteUrl = import.meta.env.VITE_CONVEX_SITE_URL as string;
        const response = await fetch(`${convexSiteUrl}/ai/edit-post`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            postId,
            originalContent,
            instruction,
          }),
          signal: controller.signal,
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'AI editing failed');
        }

        // Parse the JSON response (non-streaming for now)
        const result = await response.json();

        if (!result.success || !result.editedContent) {
          throw new Error(result.error || 'AI editing failed');
        }

        const finalContent = result.editedContent;
        setEditedContent(finalContent);
        setIsEditing(false);
        setAbortController(null);

        toast.success('AI editing completed!', {
          description: 'Review the edited content and save if you like it.',
        });

        if (onSuccess) {
          onSuccess(finalContent);
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          // Request was cancelled
          setIsEditing(false);
          setAbortController(null);
          return;
        }

        setIsEditing(false);
        setAbortController(null);
        const error = err instanceof Error ? err : new Error('AI editing failed');
        setError(error);

        toast.error('AI editing failed', {
          description: error.message || 'An unexpected error occurred. Please try again.',
        });

        if (onError) {
          onError(error);
        }
      }
    },
    [postId, originalContent, onSuccess, onError]
  );

  /**
   * Cancel ongoing AI edit
   */
  const cancelEdit = useCallback(() => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }
    setIsEditing(false);
    setEditedContent('');
    setError(null);
  }, [abortController]);

  /**
   * Save edited content to database
   */
  const saveEdit = useCallback(
    async (content: string) => {
      if (!content.trim()) {
        const err = new Error('Cannot save empty content');
        setError(err);
        toast.error('Cannot save empty content', {
          description: 'Please generate content first before saving.',
        });
        if (onError) {
          onError(err);
        }
        return false;
      }

      setIsSaving(true);
      try {
        // Save to database with transition for non-blocking update
        await startTransition(async () => {
          await updatePostContent({
            postId: postId as Id<'posts'>,
            editedContent: content,
          });
        });

        setIsSaving(false);
        toast.success('Changes saved!', {
          description: 'Your edited post has been saved successfully.',
        });
        return true;
      } catch (err) {
        setIsSaving(false);
        const error = err instanceof Error ? err : new Error('Failed to save edit');
        setError(error);
        toast.error('Failed to save changes', {
          description: error.message || 'An error occurred while saving. Please try again.',
        });
        if (onError) {
          onError(error);
        }
        return false;
      }
    },
    [postId, updatePostContent, onError]
  );

  /**
   * Reset to original content
   */
  const reset = useCallback(() => {
    setEditedContent('');
    setError(null);
    setIsEditing(false);
    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }
  }, [abortController]);

  return {
    // Content state
    editedContent,
    hasContent: editedContent.length > 0,

    // Loading states
    isEditing,
    isSaving: isSaving || isPending,

    // Error state
    error,

    // Actions
    executeEdit,
    cancelEdit,
    saveEdit,
    reset,
  };
}
